import React, { useState } from 'react';

interface ParentalConsentProps {
  userEmail: string;
  onConsent: (parentEmail: string) => void;
  onCancel: () => void;
}

export const ParentalConsent: React.FC<ParentalConsentProps> = ({
  userEmail,
  onConsent,
  onCancel,
}) => {
  const [parentEmail, setParentEmail] = useState('');
  const [confirmParentEmail, setConfirmParentEmail] = useState('');
  const [relationship, setRelationship] = useState<'parent' | 'guardian'>('parent');
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!parentEmail || !confirmParentEmail) {
      setError('Please enter parent/guardian email');
      return;
    }

    if (parentEmail !== confirmParentEmail) {
      setError('Email addresses do not match');
      return;
    }

    if (parentEmail.toLowerCase() === userEmail.toLowerCase()) {
      setError('Parent/guardian email must be different from your email');
      return;
    }

    if (!acknowledged) {
      setError('Please acknowledge the consent requirements');
      return;
    }

    onConsent(parentEmail);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-amber-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold text-rowing-gray-900">Parental Consent Required</h2>
        <p className="mt-2 text-rowing-gray-600">Users under 18 need parent or guardian consent</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}

        <div>
          <label className="block text-sm font-medium text-rowing-gray-700 mb-2">
            I need consent from my:
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setRelationship('parent')}
              className={`py-2 px-4 rounded-lg border-2 font-medium transition-colors ${
                relationship === 'parent'
                  ? 'border-rowing-blue bg-rowing-blue text-white'
                  : 'border-rowing-gray-300 text-rowing-gray-700 hover:border-rowing-gray-400'
              }`}
            >
              Parent
            </button>
            <button
              type="button"
              onClick={() => setRelationship('guardian')}
              className={`py-2 px-4 rounded-lg border-2 font-medium transition-colors ${
                relationship === 'guardian'
                  ? 'border-rowing-blue bg-rowing-blue text-white'
                  : 'border-rowing-gray-300 text-rowing-gray-700 hover:border-rowing-gray-400'
              }`}
            >
              Guardian
            </button>
          </div>
        </div>

        <div>
          <label
            htmlFor="parentEmail"
            className="block text-sm font-medium text-rowing-gray-700 mb-2"
          >
            {relationship === 'parent' ? "Parent's" : "Guardian's"} Email Address
          </label>
          <input
            id="parentEmail"
            type="email"
            value={parentEmail}
            onChange={(e) => setParentEmail(e.target.value)}
            required
            className="w-full px-4 py-3 border border-rowing-gray-300 rounded-lg focus:ring-2 focus:ring-rowing-blue focus:border-transparent transition-colors"
            placeholder="parent@example.com"
          />
        </div>

        <div>
          <label
            htmlFor="confirmParentEmail"
            className="block text-sm font-medium text-rowing-gray-700 mb-2"
          >
            Confirm {relationship === 'parent' ? "Parent's" : "Guardian's"} Email
          </label>
          <input
            id="confirmParentEmail"
            type="email"
            value={confirmParentEmail}
            onChange={(e) => setConfirmParentEmail(e.target.value)}
            required
            className="w-full px-4 py-3 border border-rowing-gray-300 rounded-lg focus:ring-2 focus:ring-rowing-blue focus:border-transparent transition-colors"
            placeholder="parent@example.com"
          />
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-amber-900 mb-2">What happens next:</h3>
          <ol className="text-xs text-amber-800 space-y-1 list-decimal list-inside">
            <li>We'll send a consent request to your {relationship}</li>
            <li>They'll need to verify their identity and approve your account</li>
            <li>You'll receive an email once consent is granted</li>
            <li>Your account will have some features limited until you turn 18</li>
          </ol>
        </div>

        <div className="flex items-start">
          <input
            id="acknowledge"
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-1 h-4 w-4 text-rowing-blue border-rowing-gray-300 rounded focus:ring-rowing-blue"
          />
          <label htmlFor="acknowledge" className="ml-3 text-sm text-rowing-gray-700">
            I understand that my {relationship} will receive an email to verify their identity and
            consent to my use of Glyyd for rowing recruitment purposes.
          </label>
        </div>

        <div className="flex space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-3 border border-rowing-gray-300 text-rowing-gray-700 rounded-lg hover:bg-rowing-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button type="submit" className="flex-1 btn-primary py-3">
            Send Consent Request
          </button>
        </div>
      </form>
    </div>
  );
};
